/**
 * Top greeting banner backgrounds — brand tint or a picked illustration.
 *
 * ## LIVE HERO (Figma `banner` 344:994)
 *
 * Banner frame: 2428 × 316 px (@2x) · live 1214 × 158 px
 *
 * Illustration is **not** full-bleed. It sits in a positioned slot
 * (`ChatGPT Image…` 344:995):
 *
 * - left: 613 px (@2x)
 * - top: 15 px (@2x)
 * - width: 1115 px (@2x)
 * - height: 372 px (@2x) — taller than the banner; clip grounds the scene
 * - image: object-cover, bottom-weighted inside the slot
 *
 * Ship source PNGs at native export (~2172 × 724). CSS placement matches Figma.
 *
 * Assets live under `/public/Illustration/top-greeting/`.
 */

import type { WorkspaceRole } from "@/lib/login-session"

/** Measured live banner size at desktop (1×). */
export const GREETING_BANNER_LIVE_SIZE = {
  width: 1214,
  height: 158,
} as const

/** Banner frame @2x (Figma `banner` 344:994). */
export const GREETING_BANNER_ART_CANVAS = {
  width: 2428,
  height: 316,
} as const

export const GREETING_BANNER_ASPECT_RATIO = 1214 / 158

/** Typical illustrator export before CSS placement. */
export const GREETING_BANNER_SOURCE_CANVAS = {
  width: 2172,
  height: 724,
} as const

/** Figma illustration slot (344:995) in @2x banner coordinates. */
export const GREETING_BANNER_HERO_SLOT_AT_2X = {
  left: 613,
  top: 15,
  width: 1115,
  height: 372,
} as const

/** Live content padding at sm+ (`p-5`). */
export const GREETING_BANNER_CONTENT_PADDING_PX = 20

/** Left of slot ≈ greeting copy clearance (@2x). */
export const GREETING_BANNER_GREETING_SAFE_ZONE = {
  startPercent: 0,
  endPercent: Math.round(
    (GREETING_BANNER_HERO_SLOT_AT_2X.left / GREETING_BANNER_ART_CANVAS.width) *
      100,
  ),
} as const

/**
 * Live shell — width-driven height at 1214:158 (Figma banner aspect).
 */
export const GREETING_BANNER_SHELL_CLASS =
  "relative flex w-full min-w-0 aspect-[1214/158] flex-col overflow-hidden rounded-xl border border-border font-sans shadow-xs"

export const GREETING_BANNER_HERO_WRAPPER_CLASS =
  "pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]"

export const GREETING_BANNER_HERO_SLOT_CLASS = "absolute"

export const GREETING_BANNER_HERO_SLOT_STYLE = {
  left: `${(GREETING_BANNER_HERO_SLOT_AT_2X.left / GREETING_BANNER_ART_CANVAS.width) * 100}%`,
  top: `${(GREETING_BANNER_HERO_SLOT_AT_2X.top / GREETING_BANNER_ART_CANVAS.height) * 100}%`,
  width: `${(GREETING_BANNER_HERO_SLOT_AT_2X.width / GREETING_BANNER_ART_CANVAS.width) * 100}%`,
  height: `${(GREETING_BANNER_HERO_SLOT_AT_2X.height / GREETING_BANNER_ART_CANVAS.height) * 100}%`,
} as const

export const GREETING_BANNER_HERO_IMAGE_CLASS =
  "pointer-events-none absolute inset-0 size-full max-w-none object-cover"

export const GREETING_BANNER_HERO_IMAGE_STYLE = {
  objectFit: "cover",
  objectPosition: "center bottom",
} as const

export const GREETING_BANNER_THUMB_ILLUSTRATION_CLASS =
  "size-full rounded-md object-cover"

export type GreetingBannerIllustrationId =
  | "brand-tint"
  | "campus-walk"
  | "team-collab"
  | "career-journey"
  | "clinical-training"
  | "patient-care"
  | "pathways"

export type GreetingBannerIllustrationOption = {
  id: GreetingBannerIllustrationId
  label: string
  description: string
  /** Public URL path, or null for the default brand wash. */
  src: string | null
}

export const GREETING_BANNER_ILLUSTRATIONS: GreetingBannerIllustrationOption[] = [
  {
    id: "brand-tint",
    label: "Brand tint",
    description: "Solid wash with optional Leo blobs",
    src: null,
  },
  {
    id: "campus-walk",
    label: "Campus & mentorship",
    description: "Full banner, student and faculty mentor",
    src: "/Illustration/top-greeting/campus-walk.png",
  },
  {
    id: "team-collab",
    label: "Team collaboration",
    description: "Full banner, clinical team at work",
    src: "/Illustration/top-greeting/team-collab.png",
  },
  {
    id: "career-journey",
    label: "Learning journey",
    description: "Full banner, education milestones",
    src: "/Illustration/top-greeting/career-journey.png",
  },
  {
    id: "clinical-training",
    label: "Clinical training",
    description: "Full banner, preceptor and student",
    src: "/Illustration/top-greeting/clinical-training.png",
  },
  {
    id: "patient-care",
    label: "Patient care",
    description: "Full banner, bedside visit",
    src: "/Illustration/top-greeting/patient-care.png",
  },
  {
    id: "pathways",
    label: "Career pathways",
    description: "Full banner, school to clinic journey",
    src: "/Illustration/top-greeting/pathways.png",
  },
]

export const DEFAULT_GREETING_BANNER_ILLUSTRATION: GreetingBannerIllustrationId =
  "brand-tint"

/** Default illustration when none is saved for the active workspace identity. */
export const GREETING_BANNER_DEFAULT_BY_ROLE = {
  student: "career-journey",
  member: "clinical-training",
  administrator: "team-collab",
} as const satisfies Record<WorkspaceRole, GreetingBannerIllustrationId>

export function resolveGreetingBannerIllustrationForRole(
  role: WorkspaceRole,
  saved: Partial<Record<WorkspaceRole, GreetingBannerIllustrationId>> = {},
): GreetingBannerIllustrationId {
  return saved[role] ?? GREETING_BANNER_DEFAULT_BY_ROLE[role]
}

export function isGreetingBannerIllustrationId(
  value: unknown,
): value is GreetingBannerIllustrationId {
  return GREETING_BANNER_ILLUSTRATIONS.some(option => option.id === value)
}

export function greetingBannerIllustrationOption(
  id: GreetingBannerIllustrationId,
): GreetingBannerIllustrationOption {
  return (
    GREETING_BANNER_ILLUSTRATIONS.find(option => option.id === id) ??
    GREETING_BANNER_ILLUSTRATIONS[0]
  )
}
